import { NavLink, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { ChevronDown, BookMarked, Home, Layers } from "lucide-react";
import { chapters } from "@/data/tutorials";
import { storage } from "@/lib/storage";

const ProgressRing = ({ done, total }) => {
  const r = 8;
  const c = 2 * Math.PI * r;
  const pct = total ? done / total : 0;
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      className="flex-shrink-0"
      aria-hidden
    >
      <circle
        cx="11"
        cy="11"
        r={r}
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="2"
        fill="none"
      />
      <circle
        cx="11"
        cy="11"
        r={r}
        stroke="hsl(var(--brut-yellow))"
        strokeWidth="3"
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform="rotate(-90 11 11)"
        strokeLinecap="butt"
      />
    </svg>
  );
};

export const Sidebar = ({ onNavigate }) => {
  const { pathname } = useLocation();
  const completed = new Set(storage.getCompleted());
  // Determine which chapter contains current tutorial
  const currentChapter = (() => {
    for (const ch of chapters) {
      for (const s of ch.sections) {
        if (pathname.includes(s.slug)) return ch.chapter;
      }
    }
    return null;
  })();

  const [open, setOpen] = useState(() => {
    const init = {};
    chapters.forEach((c) => {
      init[c.chapter] = c.chapter === currentChapter || c.chapter === 1;
    });
    return init;
  });

  return (
    <aside
      className="w-full lg:w-[320px] lg:flex-shrink-0 lg:h-screen lg:sticky lg:top-0 lg:overflow-y-auto border-r-2 border-foreground bg-background"
      data-testid="sidebar"
    >
      <div className="px-5 py-6 border-b-2 border-foreground">
        <Link
          to="/"
          onClick={onNavigate}
          className="block group"
          data-testid="sidebar-logo-link"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            BRAC Bank
          </p>
          <h1 className="font-display font-black text-2xl leading-none mt-1">
            CMS<span className="text-brut-yellow">.</span>docs
          </h1>
          <p className="font-mono text-[10px] mt-1 text-muted-foreground">
            v1.1 · interactive tutorials
          </p>
        </Link>
      </div>

      <nav className="px-3 py-4 space-y-1">
        <NavLink
          to="/"
          end
          onClick={onNavigate}
          data-testid="nav-home"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 text-sm font-semibold border-2 border-transparent ${
              isActive
                ? "bg-brut-yellow border-foreground brut-shadow-sm"
                : "hover:border-foreground hover:bg-muted"
            }`
          }
        >
          <Home className="w-4 h-4" strokeWidth={2.5} /> Overview
        </NavLink>
        <NavLink
          to="/bookmarks"
          onClick={onNavigate}
          data-testid="nav-bookmarks"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 text-sm font-semibold border-2 border-transparent ${
              isActive
                ? "bg-brut-yellow border-foreground brut-shadow-sm"
                : "hover:border-foreground hover:bg-muted"
            }`
          }
        >
          <BookMarked className="w-4 h-4" strokeWidth={2.5} /> Bookmarks
        </NavLink>
      </nav>

      <div className="px-5 pb-2 pt-3 flex items-center gap-2">
        <Layers className="w-3.5 h-3.5" strokeWidth={2.5} />
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Chapters
        </p>
      </div>

      <div className="pb-10">
        {chapters.map((ch) => {
          const done = ch.sections.filter((s) => completed.has(s.slug)).length;
          const isOpen = open[ch.chapter];
          return (
            <div key={ch.chapter} className="border-t-2 border-foreground/10">
              <button
                onClick={() =>
                  setOpen((o) => ({ ...o, [ch.chapter]: !o[ch.chapter] }))
                }
                data-testid={`sidebar-chapter-toggle-${ch.chapter}`}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-muted transition-colors"
              >
                <ProgressRing done={done} total={ch.sections.length} />
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {String(ch.chapter).padStart(2, "0")}
                  </p>
                  <p className="font-display font-bold text-sm leading-tight truncate">
                    {ch.chapterTitle}
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  strokeWidth={2.5}
                />
              </button>
              {isOpen && (
                <ul className="pb-2">
                  {ch.sections.map((s) => (
                    <li key={s.slug}>
                      <NavLink
                        to={`/tutorial/${s.slug}`}
                        onClick={onNavigate}
                        data-testid={`sidebar-tutorial-${s.slug}`}
                        className={({ isActive }) =>
                          `flex items-start gap-2 mx-2 px-3 py-2 text-sm border-l-2 ${
                            isActive
                              ? "bg-brut-yellow border-foreground font-bold"
                              : "border-transparent hover:bg-muted hover:border-foreground"
                          }`
                        }
                      >
                        <span className="font-mono text-[10px] mt-0.5 text-muted-foreground flex-shrink-0">
                          {s.id}
                        </span>
                        <span className="flex-1">{s.title}</span>
                        {completed.has(s.slug) && (
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-brut-yellow border border-foreground mt-1.5 flex-shrink-0"
                            aria-label="completed"
                          />
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};
